import { createContext, useContext, useEffect, useState, type PropsWithChildren } from "react";
import { environment } from "@web/config/environment";

export interface BusinessConfig {
  timezone: string;
  utcOffset: string;
  currency: string;
  dateFormat: string;
  timeFormat: string;
  leadNumberPrefix: string;
  apiBaseUrl: string;
  uploadBaseUrl: string;
}

const bootstrapConfig: BusinessConfig = {
  timezone: "Asia/Dubai",
  utcOffset: "+04:00",
  currency: "AED",
  dateFormat: "DD MMM YYYY",
  timeFormat: "12_HOUR",
  leadNumberPrefix: "SRMI",
  apiBaseUrl: environment.apiBaseUrl,
  uploadBaseUrl: environment.uploadBaseUrl,
};

let activeConfig = bootstrapConfig;
const BusinessConfigContext = createContext<BusinessConfig>(bootstrapConfig);

function resolveServiceUrl(value: string | undefined, fallback: string) {
  if (!value) return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  if (!/^https?:\/\//i.test(fallback)) return value;

  return new URL(value, fallback).toString().replace(/\/$/, "");
}

export function mergeBusinessConfig(
  base: BusinessConfig,
  incoming: Partial<BusinessConfig>,
): BusinessConfig {
  const apiBaseUrl = resolveServiceUrl(incoming.apiBaseUrl, base.apiBaseUrl);
  const uploadBaseUrl = resolveServiceUrl(
    incoming.uploadBaseUrl,
    apiBaseUrl,
  );

  return { ...base, ...incoming, apiBaseUrl, uploadBaseUrl };
}

export function getBusinessConfig() {
  return activeConfig;
}

export function BusinessConfigProvider({ children }: PropsWithChildren) {
  const [config, setConfig] = useState(activeConfig);
  useEffect(() => {
    let mounted = true;
    void fetch(`${environment.apiBaseUrl.replace(/\/$/, "")}/config/public`, {
      headers: { Accept: "application/json" },
    }).then((response) => response.ok ? response.json() : Promise.reject(new Error("Configuration unavailable")))
      .then((response: { data: BusinessConfig }) => {
        if (!mounted) return;
        activeConfig = mergeBusinessConfig(bootstrapConfig, response.data);
        setConfig(activeConfig);
      }).catch(() => undefined);
    return () => { mounted = false; };
  }, []);
  return <BusinessConfigContext.Provider value={config}>{children}</BusinessConfigContext.Provider>;
}

export function useBusinessConfig() {
  return useContext(BusinessConfigContext);
}
